import React, { useEffect, useState, useMemo } from 'react';
import { Spinner } from '../../../../../common/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { useTranslation } from 'react-i18next';
import {
  useGetErasmusApplicationsQuery,
  useSaveErasmusApplicationMutation,
  useGenerateErasmusExcelMutation,
} from '../../../../../../redux/services/erasmusApplications';
import { useNavigate } from 'react-router-dom';
import ErazmusApplicationSortOrder from './ErasmusApplicationSortOrder/ErazmusApplicationSortOrder';
import { FileExcelOutlined } from '@ant-design/icons';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { useGetErasmusMobilityTypesQuery } from '../../../../../../redux/services/erasmusMobilityTypes';
import { useGetErasmusMobilitySubtypesQuery } from '../../../../../../redux/services/erasmusMobilitySubtypes';
import { useGetErasmusChallengesQuery } from '../../../../../../redux/services/erasmusChallenges';
import { Tooltip } from 'antd';

const getStudyProgramId = (sp) => sp?.id ?? sp?.['id'] ?? sp;

const isFilterEmpty = (value) => value === null || value === undefined;
const isFilterAll = (value) => value === '';
const getChallengeLabel = (challenge) =>
  [challenge?.name, challenge?.academic_year].filter(Boolean).join(' - ');

const addKeyToApplication = (applications) => {
  return applications
    .map((application, idx) => ({
      key: application.id,
      ...application,
      position:
        application.position && application.position > 0
          ? application.position
          : idx + 1,
    }))
    .sort((a, b) => a.position - b.position);
};

const ErasmusApplicationOrderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: applications, isLoading } =
    useGetErasmusApplicationsQuery(false);
  const { data: erasmusMobilityTypes } = useGetErasmusMobilityTypesQuery();
  const { data: erasmusMobilitySubtypes } = useGetErasmusMobilitySubtypesQuery();
  const { data: erasmusChallenges } = useGetErasmusChallengesQuery();

  const [applicationsSort, setApplicationsSort] = useState([]);
  const [type, setType] = useState(null);
  const [subType, setSubType] = useState(null);
  const [challenge, setChallenge] = useState(null);

  const filteredApplications = useMemo(() => {
    if (!applications) return [];

    const sentApplications = applications.filter(
      (application) => application.applicationStatus === 'Odoslaná',
    );

    if (
      (isFilterEmpty(type) || isFilterAll(type)) &&
      (isFilterEmpty(subType) || isFilterAll(subType)) &&
      (isFilterEmpty(challenge) || isFilterAll(challenge))
    ) {
      return addKeyToApplication(sentApplications);
    }

    const filtered = sentApplications.filter((application) => {
      return (
        (isFilterEmpty(type) ||
          isFilterAll(type) ||
          application.mobility_type?.id === type) &&
        (isFilterEmpty(subType) ||
          isFilterAll(subType) ||
          (application.mobility_subtype &&
            application.mobility_subtype.id === subType)) &&
        (isFilterEmpty(challenge) ||
          isFilterAll(challenge) ||
          application.challenge_id === challenge)
      );
    });

    return addKeyToApplication(filtered);
  }, [applications, type, subType, challenge]);

  useEffect(() => {
    setApplicationsSort(filteredApplications);
  }, [filteredApplications]);

  const { saveSuccess, apiErrorMessage } = useToastMessages();

  const [saveErasmusApplicationOrder] = useSaveErasmusApplicationMutation();

  const [generateErasmusExcel] = useGenerateErasmusExcelMutation();

  const mobilityTypesOptions = useMemo(() => {
    if (!erasmusMobilityTypes) return [];
    return [
      ...erasmusMobilityTypes.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      { value: '', label: t('--Všetky--') },
    ];
  }, [erasmusMobilityTypes]);

  const mobilitySubypesOptions = useMemo(() => {
    if (!erasmusMobilitySubtypes) return [];
    return [
      ...erasmusMobilitySubtypes.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      { value: '', label: t('--Všetky--') },
    ];
  }, [erasmusMobilitySubtypes]);

  const challengeOptions = useMemo(() => {
    if (!erasmusChallenges) return [];
    return [
      ...erasmusChallenges.map((item) => ({
        value: item.id,
        label: getChallengeLabel(item),
      })),
      { value: '', label: t('--Všetky--') },
    ];
  }, [erasmusChallenges]);

  const handleTypeChange = (newValue) => {
    setType(newValue);
  };

  const handleSubTypeChange = (newValue) => {
    setSubType(newValue);
  };

  const handleChallengeChange = (newValue) => {
    setChallenge(newValue);
  };

  return isLoading && !applications ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Prihlášky')}
        backUrl={routes.cmsErasmusApplication}
        backUrlTooltip={t('Tabuľka prihlášok')}
        permissionsBasic="erasmus-applications"
        mainActions={[
          {
            onClick: async () => {
              try {
                const response = await generateErasmusExcel({
                  body: applicationsSort,
                }).unwrap();

                const byteCharacters = atob(response.excel);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                  type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });

                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'Nomincie_mobility.xlsx';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
              } catch (error) {
                apiErrorMessage(error);
              }
            },

            icon: (
              <Tooltip
                title={t('Nominácie na mobility')}
                placement="bottomLeft"
              >
                <FileExcelOutlined />
              </Tooltip>
            ),
            // premision: 'erasmus-applications.excel',
            permission: 'erasmus-applications.destroy',
          },
        ]}
      />

      <ErazmusApplicationSortOrder
        applicationsSort={applicationsSort}
        setApplicationsSort={setApplicationsSort}
        onOrderSave={async (applicationsData) => {
          await Promise.all(
            applicationsData.map((application) =>
              saveErasmusApplicationOrder({
                body: {
                  ...application,
                  study_program: getStudyProgramId(application?.study_program),
                },
                id: application.id,
              }).unwrap(),
            ),
          );

          saveSuccess();
          navigate(routes.cmsErasmusApplication);
        }}
        mobilityTypesOptions={mobilityTypesOptions}
        mobilitySubypesOptions={mobilitySubypesOptions}
        challengeOptions={challengeOptions}
        onTypeChange={handleTypeChange}
        onSubTypeChange={handleSubTypeChange}
        onChallengeChange={handleChallengeChange}
        type={type}
        subType={subType}
        challenge={challenge}
      />
    </>
  );
};

export default ErasmusApplicationOrderPage;
